(() => {
  const request = async (url, options = {}, format = 'json') => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {...options, signal: controller.signal});
      const data = format === 'text' ? await response.text() : await response.json();
      return {ok: response.ok, data};
    } finally {
      clearTimeout(timeout);
    }
  };
  const signature = items => JSON.stringify(items.map(item => [item.key, Number(item.variant_id || item.id), item.quantity]).sort((a, b) => a[0].localeCompare(b[0])));
  const readValidation = root => {
    const node = root.querySelector('[data-pbx-cart-validation]');
    const data = JSON.parse(node?.textContent || 'null');
    if (data?.version !== 1 || typeof data.ready !== 'boolean' || !Array.isArray(data.items) || data.items.some(item => typeof item.key !== 'string' || !item.key || !Number.isSafeInteger(item.variant_id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !['valid', 'invalid', 'not_ready'].includes(item.status)) || new Set(data.items.map(item => item.key)).size !== data.items.length) throw new Error('pbx_cart_validation');
    return data;
  };
  if (!window.PostbusCartValidation) window.PostbusCartValidation = Object.freeze({
    read: readValidation,
    async ensure({cartUrl, expectedItems}) {
      try {
        const url = new URL(cartUrl, location.origin);
        if (url.origin !== location.origin) throw new Error('pbx_cart_validation');
        url.search = '';
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await request(url, {credentials: 'same-origin', cache: 'no-store'}, 'text');
        if (!response.ok) throw new Error('pbx_cart_validation');
        const data = readValidation(new DOMParser().parseFromString(response.data, 'text/html'));
        if (!data.ready) throw new Error('pbx_cart_closed');
        if (!data.items.length || expectedItems && signature(data.items) !== signature(expectedItems)) throw new Error('pbx_cart_changed');
        if (data.items.some(item => item.status !== 'valid')) throw new Error('pbx_cart_blocked');
        return data;
      } catch (error) {
        if (['pbx_cart_validation', 'pbx_cart_closed', 'pbx_cart_changed', 'pbx_cart_blocked'].includes(error.message)) throw error;
        throw new Error('pbx_cart_validation');
      }
    },
    message(error, root) {
      const messages = {pbx_cart_validation: 'pbxValidation', pbx_cart_closed: 'pbxClosed', pbx_cart_changed: 'pbxChanged', pbx_cart_blocked: 'pbxBlocked'};
      return root.dataset[messages[error.message]] || '';
    }
  });
  if (customElements.get('pbx-cart')) return;
  class PostbusCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.busy = false;
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-pbx-remove]');
        const step = event.target.closest('[data-pbx-step]');
        if (!remove && !step) return;
        event.preventDefault();
        if (this.busy) return;
        const row = event.target.closest('[data-pbx-cart-item]');
        const input = row.querySelector('[data-pbx-quantity]');
        const entered = input.value.trim();
        const current = Number(entered);
        if (!remove && (entered === '' || !Number.isSafeInteger(current) || current < 0)) { input.value = row.dataset.quantity; return; }
        const quantity = remove ? 0 : Math.max(0, current + Number(step.dataset.pbxStep));
        this.change(row.dataset.key, quantity);
      });
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-pbx-quantity]') || this.busy) return;
        const row = event.target.closest('[data-pbx-cart-item]');
        const entered = event.target.value.trim();
        const quantity = Number(entered);
        if (entered === '' || !Number.isSafeInteger(quantity) || quantity < 0) { event.target.value = row.dataset.quantity; return; }
        if (quantity !== Number(row.dataset.quantity)) this.change(row.dataset.key, quantity);
      });
      this.addEventListener('focusout', event => {
        if (!event.target.matches('[data-pbx-quantity]')) return;
        const entered = event.target.value.trim();
        const quantity = Number(entered);
        if (entered === '' || !Number.isSafeInteger(quantity) || quantity < 0) {
          event.target.value = event.target.closest('[data-pbx-cart-item]').dataset.quantity;
        }
      });
      this.addEventListener('submit', event => {
        if (this.busy) { event.preventDefault(); return; }
        if (event.submitter?.name !== 'checkout' || this.checkoutApproved) return;
        event.preventDefault();
        void this.checkout(event.target, event.submitter);
      });
    }
    async checkout(form, submitter) {
      this.lock(true);
      let approved = false;
      let current = this;
      try {
        const initial = window.PostbusCartValidation.read(this);
        await window.PostbusCartValidation.ensure({cartUrl: this.dataset.cartUrl, expectedItems: initial.items});
        approved = true;
      } catch (error) {
        let message = window.PostbusCartValidation.message(error, this) || this.dataset.pbxValidation;
        if (error.message === 'pbx_cart_changed') {
          try {
            current = await this.refresh() || this;
            if (current !== this) message = current.dataset.pbxChangedUpdated || message;
          } catch (_) {}
        }
        current.notice(message, true);
      } finally {
        if (this.isConnected) this.lock(false);
      }
      if (approved && this.isConnected) {
        this.checkoutApproved = true;
        try { form.requestSubmit(submitter); }
        finally { this.checkoutApproved = false; }
      }
    }
    lock(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('button,input').forEach(control => {
        if (value) { control.dataset.pbxDisabled = String(control.disabled); control.disabled = true; }
        else if ('pbxDisabled' in control.dataset) { control.disabled = control.dataset.pbxDisabled === 'true'; delete control.dataset.pbxDisabled; }
      });
    }
    notice(message, error = false) {
      const target = this.querySelector('[data-pbx-cart-notice]');
      target.textContent = message;
      target.hidden = false;
      target.classList.toggle('is-error', error);
      target.setAttribute('role', error ? 'alert' : 'status');
      if (error) {
        target.tabIndex = -1;
        target.focus();
      }
    }
    replace(html, key) {
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const replacement = doc.querySelector('pbx-cart');
      if (!replacement) return null;
      let cart;
      try { cart = readValidation(replacement); } catch (_) { return null; }
      this.replaceWith(replacement);
      document.dispatchEvent(new CustomEvent('postbus:cart-updated', { detail:{ item_count:cart.items.reduce((sum, item) => sum + item.quantity, 0) } }));
      const row = [...replacement.querySelectorAll('[data-pbx-cart-item]')].find(item => item.dataset.key === key);
      const focus = row?.querySelector('[data-pbx-quantity]') || replacement.querySelector('h1');
      focus?.focus({preventScroll:true});
      return replacement;
    }
    async refresh(key) {
      const url = new URL(this.dataset.cartUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await request(url, {credentials:'same-origin',cache:'no-store'}, 'text');
      if (!response.ok) throw new Error(this.dataset.error);
      return this.replace(response.data, key);
    }
    async change(key, quantity) {
      if (this.busy || !Number.isSafeInteger(quantity) || quantity < 0) return;
      this.lock(true);
      let current = this;
      try {
        const base = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : this.dataset.rootUrl + '/';
        const response = await request(base + 'cart/change.js', {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({id:key,quantity,sections:[this.dataset.sectionId],sections_url:location.pathname + location.search})});
        const data = response.data;
        if (!response.ok) {
          current = await this.refresh(key) || this;
          throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : this.dataset.error);
        }
        current = this.replace(data.sections?.[this.dataset.sectionId], key) || await this.refresh(key) || this;
        current.notice(current.dataset.updated);
        document.dispatchEvent(new CustomEvent('postbus:cart-updated', {detail:{cart:data}}));
      } catch (error) {
        if (current === this && this.isConnected) {
          try { current = await this.refresh(key) || this; } catch (_) {}
        }
        const message = ['AbortError', 'TimeoutError', 'TypeError', 'SyntaxError'].includes(error.name) ? current.dataset.error : error.message || current.dataset.error;
        current.notice(message, true);
      } finally {
        if (this.isConnected) this.lock(false);
      }
    }
  }
  customElements.define('pbx-cart', PostbusCart);
})();
